import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Box, Button, Card, CardContent, Typography, List, ListItem, ListItemText, Divider, Dialog, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { Refresh, Preview, Close } from '@mui/icons-material';
import { shipment } from '../../../../../../models/entities/shipment/shipment';
import { operatorTrackingEvent, operatorTrackingPreviewResponse } from '../../../../../../models/entities/shipment/operatorTracking';
import { localSDK } from '../../../../../../sdk';
import Loader from '../../../../../shared/SmallComponents/Loader';
import { styled } from '../../../../../shared/Theme/theme';
import moment from 'moment';

type Props = {
    shipment: shipment;
    localization?: any;
    onRefreshComplete?: () => void;
    onClose?: () => void;
};

const SectionContainer = styled(Box)`
    margin-bottom: 20px;
    padding: 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #f9f9f9;
`;

const ButtonContainer = styled(Box)`
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
`;


const EventCard = styled(Card)`
    width: 100%;
    margin-bottom: 8px;
    border-left: 4px solid #1976d2;
    &.delivery { border-left-color: #4caf50; }
    &.arrival { border-left-color: #ff9800; }
    &.customs { border-left-color: #9c27b0; }
    &.pickup { border-left-color: #2196f3; }
    &.onboard { border-left-color: #607d8b; }
    &.onhand { border-left-color: #795548; }
    &.delivered { border-left-color: #2e7d32; }
    &.exception { border-left-color: #f44336; }
`;

const EventHeader = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
    width: 100%;
`;

const EventDate = styled(Typography)`
    color: #666;
    font-size: 0.875rem;
    white-space: nowrap;
    font-weight: 500;
    flex-shrink: 0;
    margin-left: 16px;
`;

const ModalHeader = styled(Box)`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid #e0e0e0;
`;

const ModalActions = styled(Box)`
    display: flex;
    gap: 12px;
    align-items: center;
`;

const ScrollableContent = styled(Box)`
    max-height: 60vh;
    overflow-y: auto;
    padding: 16px 0;
`;

const OperatorTrackingSection: React.FC<Props> = ({ shipment, localization, onRefreshComplete, onClose }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [previewData, setPreviewData] = useState<operatorTrackingPreviewResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    // If onClose is provided, this is being used as a modal from the top level
    const isModalMode = !!onClose;
    
    const handlePreview = useCallback(async () => {
        if (!shipment.id) return;
        
        setIsLoading(true);
        setError(null);
        setIsModalOpen(true);
        
        try {
            const response = await localSDK.shipments().operatorTrackingPreview({
                shipmentId: shipment.id
            });
            setPreviewData(response);
        } catch (err) {
            console.error('Error fetching operator tracking preview:', err);
            setError('Failed to fetch tracking preview. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [shipment.id]);
    
    // Auto-open modal and fetch data when component mounts in modal mode
    useEffect(() => {
        if (isModalMode) {
            setIsModalOpen(true);
            // Reset state and fetch data
            setPreviewData(null);
            setError(null);
            setIsLoading(false);
            setIsRefreshing(false);
            handlePreview();
        }
    }, [isModalMode, handlePreview]);

    const getEventTypeClass = (eventCode: string, comment?: string): string => {
        const code = eventCode.toLowerCase();
        const commentCode = comment?.toLowerCase() || '';
        
        // Map backend event codes to CSS classes for styling
        // These codes now come from the updated backend mapping aligned with the guide
        switch (code) {
            // Order Confirmation (before pickup)
            case 'order': return 'onhand';
            
            // Booking (picked up, still in origin country)
            case 'pick_up': return 'pickup';
            
            // On Board (left origin country, in transit)
            case 'goods_on_board': return 'onboard';
            
            // Arrivals (reached destination country)
            case 'arrival': return 'arrival';
            
            // Customs (in destination country)
            case 'customs_event': return 'customs';
            
            // Customs Released (cleared customs)
            case 'customs_released': return 'customs';
            
            // Delivery (out for delivery)
            case 'delivery': return 'delivery';
            
            // Delivered (proof of delivery)
            case 'pod': return 'delivered';
            
            // Legacy mappings for backward compatibility
            case 'on_hand': return 'onhand';
            
            default:
                // Fallback to comment-based mapping for unknown codes
                switch (commentCode) {
                    case 'pu': case 'pick': case 'pickup': return 'pickup';
                    case 'oc': case 'on_hand': return 'onhand';
                    case 'it': case 'dp': case 'ao': case 'goods_on_board': case 'transit': return 'onboard';
                    case 'ar': case 'arrival': return 'arrival';
                    case 'ha': case 'rr': case 'customs': return 'customs';
                    case 'od': case 'hp': case 'delivery': return 'delivery';
                    case 'dl': case 'delivered': return 'delivered';
                    case 'se': case 'shipment_exception': return 'exception';
                    default: return 'onboard';
                }
        }
    };


    const handleSync = async () => {
        if (!shipment.id) return;
        
        setIsRefreshing(true);
        setError(null);
        
        try {
            await localSDK.shipments().operatorTrackingRefresh({
                shipmentId: shipment.id
            });
            
            // Close modal and reset state before calling onRefreshComplete
            setIsModalOpen(false);
            setPreviewData(null);
            setError(null);
            setIsLoading(false);
            
            // Call onRefreshComplete to update parent component and close modal
            if (onRefreshComplete) {
                onRefreshComplete();
            }
        } catch (err) {
            console.error('Error syncing operator tracking:', err);
            setError('Failed to sync tracking events. Please try again.');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setPreviewData(null);
        setError(null);
        if (isModalMode && onClose) {
            onClose();
        }
    };

    const renderPreviewEvents = () => {
        if (!previewData?.events?.length) return null;

        return (
            <List>
                {previewData.events.map((event: operatorTrackingEvent, index: number) => (
                    <React.Fragment key={index}>
                        <ListItem>
                            <EventCard className={getEventTypeClass(event.eventCode, event.comment)}>
                                <CardContent>
                                    <EventHeader>
                                        <Typography variant="h6" component="div">
                                            {event.description}
                                        </Typography>
                                        <EventDate variant="body2">
                                            {moment(event.eventDate).format('DD/MM/YYYY HH:mm')}
                                        </EventDate>
                                    </EventHeader>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Event Code: {event.eventCode}
                                        {event.comment && (
                                            <span style={{ marginLeft: '8px', color: '#666' }}>
                                                (Comment: {event.comment})
                                            </span>
                                        )}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        By: {event.userName}
                                    </Typography>
                                </CardContent>
                            </EventCard>
                        </ListItem>
                        {index < previewData.events.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </List>
        );
    };

    return (
        <>
            {!isModalMode && (
                <SectionContainer>
                    <Typography variant="h6" gutterBottom>
                        FedEx Operator Tracking
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Sync tracking events directly from FedEx for this shipment.
                    </Typography>
                    
                    <ButtonContainer>
                        <Button
                            variant="outlined"
                            startIcon={<Preview />}
                            onClick={handlePreview}
                            disabled={isLoading || isRefreshing}
                        >
                            Preview Events
                        </Button>
                    </ButtonContainer>
                </SectionContainer>
            )}

            <Dialog 
                open={isModalOpen} 
                onClose={handleCloseModal}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    style: { minHeight: '400px' }
                }}
            >
                <ModalHeader>
                    <Box>
                        <Typography variant="h6">
                            {previewData?.isMultiPackage ? 'Multi-Package Shipment' : 'FedEx Tracking Events Preview'}
                        </Typography>
                        {previewData && !previewData.isMultiPackage && (
                            <Typography variant="body2" color="text.secondary">
                                {previewData.events.length} events found
                            </Typography>
                        )}
                        {previewData?.isMultiPackage && (
                            <Typography variant="body2" color="text.secondary">
                                {previewData.packageCount} packages detected
                            </Typography>
                        )}
                    </Box>
                    <ModalActions>
                        {previewData && !previewData.isMultiPackage && (
                            <Button
                                variant="contained"
                                startIcon={isRefreshing ? <Loader wrapperWidth="auto" showText={false} marginTop="0" width="15px" /> : <Refresh />}
                                onClick={handleSync}
                                disabled={isLoading || isRefreshing}
                                color="primary"
                            >
                                {isRefreshing ? 'Syncing...' : 'Sync Events'}
                            </Button>
                        )}
                        <IconButton onClick={handleCloseModal} size="small">
                            <Close />
                        </IconButton>
                    </ModalActions>
                </ModalHeader>

                <DialogContent>
                    {error && (
                        <Alert severity="error" style={{ marginBottom: '16px' }}>
                            {error}
                        </Alert>
                    )}

                    {isLoading && (
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="200px">
                            <Loader width="24px" />
                            <Typography variant="body2" style={{ marginLeft: '12px' }}>
                                Fetching tracking events from FedEx...
                            </Typography>
                        </Box>
                    )}

                    {previewData && !isLoading && (
                        <ScrollableContent>
                            {previewData.isMultiPackage ? (
                                <Box display="flex" flexDirection="column" alignItems="center" padding="40px 20px" textAlign="center">
                                    <Box marginBottom={3}>
                                        <Typography variant="h5" color="primary" gutterBottom>
                                            Multi-Package Shipment Detected
                                        </Typography>
                                        <Typography variant="body1" color="text.secondary" paragraph>
                                            This shipment contains {previewData.packageCount} packages. 
                                            Automatic tracking event synchronization is not available for multi-package shipments.
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            Please use the tracking URL below to manually track and sync events for this shipment.
                                        </Typography>
                                    </Box>
                                    
                                    {previewData.externalTrackingUrl && (
                                        <Box 
                                            padding={3} 
                                            sx={{
                                                border: "1px solid #e0e0e0",
                                                borderRadius: 2,
                                                backgroundColor: "#f9f9f9",
                                                width: "100%",
                                                maxWidth: "500px"
                                            }}
                                        >
                                            <Typography variant="subtitle2" gutterBottom>
                                                FedEx Tracking URL:
                                            </Typography>
                                            <Box 
                                                component="a" 
                                                href={previewData.externalTrackingUrl} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{ 
                                                    color: '#1976d2',
                                                    textDecoration: 'none',
                                                    wordBreak: 'break-all',
                                                    display: 'block',
                                                    padding: '8px',
                                                    backgroundColor: 'white',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ddd'
                                                }}
                                            >
                                                {previewData.externalTrackingUrl}
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <>
                                    {previewData.externalTrackingUrl && (
                                        <Box marginBottom={2}>
                                            <Typography variant="body2">
                                                External Tracking:{' '}
                                                <a 
                                                    href={previewData.externalTrackingUrl} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    style={{ color: '#1976d2' }}
                                                >
                                                    {previewData.externalTrackingUrl}
                                                </a>
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    {renderPreviewEvents()}
                                </>
                            )}
                        </ScrollableContent>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default OperatorTrackingSection;
